import type { SSEEvent } from '../providers/types';

export function parseSseEvent(raw: string): SSEEvent | null {
  const lines = raw.split('\n');
  let event = '';
  let data = '';
  for (const line of lines) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  if (!event) return null;
  try {
    const parsed = data ? JSON.parse(data) : {};
    switch (event) {
      case 'token':
        return { type: 'token', text: String(parsed.text ?? '') };
      case 'usage':
        // Pick fields explicitly so an adversarial payload containing
        // "type":"token" can't override the discriminant via spread.
        return {
          type: 'usage',
          input_tokens: typeof parsed.input_tokens === 'number' ? parsed.input_tokens : undefined,
          output_tokens: typeof parsed.output_tokens === 'number' ? parsed.output_tokens : undefined,
          server_latency_ms:
            typeof parsed.server_latency_ms === 'number' ? parsed.server_latency_ms : undefined,
        };
      case 'done':
        return { type: 'done' };
      case 'error':
        return { type: 'error', code: String(parsed.code ?? 'unknown'), message: String(parsed.message ?? '') };
      default:
        return null;
    }
  } catch {
    return null;
  }
}

export async function* consumeSseStream(body: ReadableStream<Uint8Array>): AsyncGenerator<SSEEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const evt = parseSseEvent(raw);
        if (evt) yield evt;
      }
    }
    if (buffer.trim().length > 0) {
      const evt = parseSseEvent(buffer);
      if (evt) yield evt;
    }
  } finally {
    // Release the stream lock even if the caller breaks early out of the
    // for-await loop. cancel() resolves regardless of stream state.
    reader.cancel().catch(() => undefined);
  }
}
